/**
 * Custom hook for managing truck CRUD operations
 */

import React from 'react';
import { customerService } from '../../../../services/customer.service';
import { apiClient } from '../../../../services/api.service';
import { formatDateForInput } from '../utils/formatters';
import type { Truck, Customer } from '../types';

export const useTruckManagement = (customerId: string | undefined, customer: Customer | undefined) => {
  const [trucks, setTrucks] = React.useState<Truck[]>([]);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [truckToDelete, setTruckToDelete] = React.useState<Truck | null>(null);

  // Load trucks from customer data when available
  React.useEffect(() => {
    if (customer?.vehicles && Array.isArray(customer.vehicles)) {
      const mappedTrucks = customer.vehicles.map((vehicle: any) => ({
        id: vehicle._id || vehicle.id,
        // SCHEMA MIGRATION: Use truck_name instead of make/model
        name: vehicle.truck_name || vehicle.truckName || 'New Vehicle',
        vin: vehicle.vin_number || vehicle.vin || '',
        licensePlate: vehicle.license_plate || vehicle.licensePlate || '',
        registrationDate: formatDateForInput(vehicle.registrationDate || vehicle.registration_date),
        biAnnualDate: formatDateForInput(vehicle.biAnnualDate || vehicle.bi_annual_date),
      }));
      setTrucks(mappedTrucks);
    }
  }, [customer]);

  /**
   * Update truck field in database
   */
  const handleTruckUpdate = async (truckId: string, field: string, value: string) => {
    // Update local state immediately for optimistic UI
    setTrucks(prevTrucks =>
      prevTrucks.map(truck =>
        truck.id === truckId ? { ...truck, [field]: value } : truck
      )
    );

    // Update truck in database
    try {
      await customerService.updateTruck(truckId, { [field]: value });
    } catch (error) {
      console.error('Failed to update truck:', error);
      // Revert the change on error
      setTrucks(prevTrucks =>
        prevTrucks.map(truck =>
          truck.id === truckId ? { ...truck, [field]: (truck as any)[field] } : truck
        )
      );
      alert(`Failed to update truck: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  /**
   * Handle "Registration" date change with automatic "Bi-Annual" calculation (6 months ahead)
   */
  const handleRegistrationDateChange = async (truckId: string, registrationDate: string) => {
    // Calculate bi-annual date (6 months ahead)
    let biAnnualDate = '';
    if (registrationDate) {
      const date = new Date(registrationDate);
      date.setMonth(date.getMonth() + 6);
      // Format as YYYY-MM-DD for input[type="date"]
      const parts = date.toISOString().split('T');
      biAnnualDate = parts[0] || '';
    }

    // Update local state immediately for optimistic UI
    setTrucks(prevTrucks =>
      prevTrucks.map(truck =>
        truck.id === truckId
          ? { ...truck, registrationDate: registrationDate, biAnnualDate: biAnnualDate }
          : truck
      )
    );

    // Update both fields in database
    try {
      await customerService.updateTruck(truckId, {
        registrationDate: registrationDate,
        biAnnualDate: biAnnualDate,
      });
    } catch (error) {
      console.error('Failed to update truck registration dates:', error);
      // Revert the change on error
      const originalTruck = trucks.find(t => t.id === truckId);
      if (originalTruck) {
        setTrucks(prevTrucks =>
          prevTrucks.map(truck =>
            truck.id === truckId
              ? {
                  ...truck,
                  registrationDate: originalTruck.registrationDate,
                  biAnnualDate: originalTruck.biAnnualDate,
                }
              : truck
          )
        );
      }
      alert(`Failed to update registration dates: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  /**
   * Add a new truck row
   */
  const handleAddTruck = async () => {
    if (!customerId) return;

    try {
      // Create new vehicle in database
      const newVehicle = await customerService.createTruck(customerId);

      // Add to local state (handle both snake_case and camelCase field names)
      const newTruck: Truck = {
        id: newVehicle.id || newVehicle._id,
        // SCHEMA MIGRATION: Use truck_name instead of make/model
        name: newVehicle.truck_name || newVehicle.truckName || 'New Vehicle',
        vin: newVehicle.vin || newVehicle.vin_number || '',
        licensePlate: newVehicle.license_plate || newVehicle.licensePlate || '',
        registrationDate: '',
        biAnnualDate: '',
      };

      setTrucks(prevTrucks => [...prevTrucks, newTruck]);
    } catch (error) {
      console.error('Failed to add truck:', error);
      alert(`Failed to add truck: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  /**
   * Open delete confirmation modal
   */
  const handleDeleteClick = (truck: Truck) => {
    setTruckToDelete(truck);
    setDeleteModalOpen(true);
  };

  /**
   * Close delete confirmation modal
   */
  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setTruckToDelete(null);
  };

  /**
   * Confirm and execute truck deletion
   */
  const handleConfirmDelete = async () => {
    if (!truckToDelete || !customerId) return;

    try {
      // Delete truck from database
      await customerService.deleteTruck(customerId, truckToDelete.id);

      // Remove truck from local state
      setTrucks(prevTrucks =>
        prevTrucks.filter(truck => truck.id !== truckToDelete.id)
      );

      // Close modal
      setDeleteModalOpen(false);
      setTruckToDelete(null);
    } catch (error) {
      console.error('Failed to delete truck:', error);
      alert(`Failed to delete truck: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };


  /**
   * Handle selection of an unassigned vehicle from autocomplete.
   * Assigns the selected vehicle to this customer, replaces the placeholder truck,
   * and deletes the placeholder if it was a newly created empty one.
   */
  const handleVehicleSelected = async (currentTruckId: string, vehicle: any) => {
    if (!customerId) return;

    try {
      // Assign the selected vehicle to this customer via API
      await apiClient.post(`/api/v1/vehicles/${vehicle.id}/assign`, null, {
        params: { customer_id: customerId },
      });

      // Replace the current truck in local state with the selected vehicle's data
      setTrucks(prevTrucks =>
        prevTrucks.map(truck => {
          if (truck.id === currentTruckId) {
            return {
              ...truck,
              id: vehicle.id,
              name: vehicle.truckName || vehicle.truck_name || 'New Vehicle',
              vin: vehicle.vin || '',
              licensePlate: vehicle.licensePlate || vehicle.license_plate || '',
              registrationDate: vehicle.registrationDate || vehicle.registration_date || '',
              biAnnualDate: vehicle.biAnnualDate || vehicle.bi_annual_date || '',
            };
          }
          return truck;
        })
      );

      // Delete the placeholder truck if it was different from the selected one
      if (currentTruckId !== vehicle.id) {
        try {
          await customerService.deleteTruck(customerId, currentTruckId);
        } catch {
          // Placeholder may not exist in DB yet, ignore deletion errors
        }
      }
    } catch (error) {
      console.error('Failed to assign vehicle:', error);
      alert(`Failed to assign vehicle: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return {
    trucks,
    setTrucks,
    deleteModalOpen,
    truckToDelete,
    handleTruckUpdate,
    handleRegistrationDateChange,
    handleAddTruck,
    handleDeleteClick,
    handleCancelDelete,
    handleConfirmDelete,
    handleVehicleSelected,
  };
};
